import FlagIndicatorCell from '@app/src/components/Table/Cells/FlagIndicatorCell'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { SUSTAINABILITY_LIBRARY_PAGE_IDS } from '@app/src/pages/Configurations/SustainabilityLibraryScene'
import { sustainabilityAreaTranslationKeys } from '@app/src/pages/TopicalRisk/CompanyPage/TopicalRiskAreaCard'
import { ActivationType } from '@app/src/types/flags'
import type { Question, QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import { TableRow } from '@mui/material'
import qs from 'qs'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import { useHistory } from 'react-router-dom'
import QuestionnaireNameCell from './QuestionnaireNameCell'
import type { AreasByAssessmentTemplateId, QuestionnaireRowSummary } from './questionnaireRowSummary'
import { getQuestionnaireRowSummary } from './questionnaireRowSummary'
import { TemplateTypeId } from './templateTypeConfig'

export const countUniqueActiveItems = (flagRules?: Question['reportFlagRules']): number => {
  if (!flagRules?.length) {
    return 0
  }

  const uniqueItemIds = new Set<number>()

  flagRules?.forEach(flagRule => {
    if (flagRule && flagRule.activationType === ActivationType.Active) {
      uniqueItemIds.add(flagRule.questionId)
    }
  })

  return uniqueItemIds.size
}

const NO_AREAS: AreasByAssessmentTemplateId = new Map()

const formatSubtitle = (
  { kind, areas, tagCategories }: QuestionnaireRowSummary,
  formatMessage: IntlShape['formatMessage'],
): string => {
  const aiScreening = formatMessage({ id: 'general.aiScreening' })
  if (kind === TemplateTypeId.AiScreening) return aiScreening

  const categoryLabels = [
    ...areas.map(area => formatMessage({ id: sustainabilityAreaTranslationKeys[area] })),
    ...tagCategories.map(tag => formatMessage({ id: `transparencyOverview.tags.${tag}` })),
  ].join(', ')

  if (kind === TemplateTypeId.Assessment) {
    return [aiScreening, formatMessage({ id: 'resourceTypes.assessment' }), categoryLabels].filter(Boolean).join(' · ')
  }
  return [formatMessage({ id: 'configurations.questionnaire' }), categoryLabels].filter(Boolean).join(' · ')
}

interface StandardReportsRowProps {
  row: QuestionnaireTemplate
  baselineQuestionnaireId?: number
  areasByAssessmentTemplateId?: AreasByAssessmentTemplateId
}

export const StandardReportsRow: React.FC<StandardReportsRowProps> = ({
  row: requestTemplate,
  baselineQuestionnaireId,
  areasByAssessmentTemplateId = NO_AREAS,
}) => {
  const { formatMessage } = useIntl()
  const flagRules = requestTemplate.sections?.flatMap(s => s.questions).flatMap(q => q.reportFlagRules ?? []) ?? []
  const flagCount = countUniqueActiveItems(flagRules)
  const history = useHistory()
  const { solution } = useAuthentication().scope
  const summary = getQuestionnaireRowSummary(requestTemplate, {
    isBaseline: requestTemplate.id === baselineQuestionnaireId,
    solution,
    areasByAssessmentTemplateId,
  })

  const previewLocation = {
    pathname: generatePath(paths.sustainabilityLibrary, {
      configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS.Questionnaires,
      configurationsSubPage: requestTemplate.id,
    }),
    search: qs.stringify({ isStandard: true }),
  }

  return (
    <TableRow hover sx={{ '& td': { cursor: 'pointer' } }} onClick={() => history.push(previewLocation)}>
      <QuestionnaireNameCell
        kind={summary.kind}
        title={requestTemplate.title || formatMessage({ id: 'general.noTitle' })}
        subtitle={formatSubtitle(summary, formatMessage)}
        to={previewLocation}
      />
      <FlagIndicatorCell count={flagCount} bgcolor="communication.main" />
    </TableRow>
  )
}

export default StandardReportsRow
